/**
 * RptNmethProposalPage - 方法論提案申請表報告頁面
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useProject } from '../contexts/ProjectContext';
import { getNmethCompleteness, getNmethHtmlReport, downloadNmethDocxReport, NmethCompletenessData } from '../services/nmethReportService';
import FunctionPageHeader from '../components/FunctionPageHeader';
import { usePermission } from '../hooks/usePermission';
import { useToast } from '../contexts/ToastContext';
import '../styles/DataTable.css';

const RptNmethProposalPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isZh = i18n.language === 'zh-TW';
  const { selectedProposalId, selectedProposal } = useProject();
  const { hasPermission } = usePermission();
  const toast = useToast();
  const canPrint = hasPermission('rpt_nmeth_proposal', 'print');
  const canFile = hasPermission('rpt_nmeth_proposal', 'file');
  const [data, setData] = useState<NmethCompletenessData | null>(null);
  const [loading, setLoading] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadCompleteness = useCallback(async (proposalId: number) => {
    setLoading(true);
    setError(null);
    try {
      const result = await getNmethCompleteness(proposalId);
      setData(result);
    } catch (e: any) {
      setError(e?.response?.data?.detail || (isZh ? '載入失敗' : 'Load failed'));
    } finally {
      setLoading(false);
    }
  }, [isZh]);

  useEffect(() => {
    if (selectedProposalId) {
      loadCompleteness(selectedProposalId);
    } else {
      setData(null);
    }
  }, [selectedProposalId, loadCompleteness]);

  const handlePrint = async () => {
    if (!selectedProposalId) return;
    setPrinting(true);
    try {
      const html = await getNmethHtmlReport(selectedProposalId);
      const win = window.open('', '_blank');
      if (win) {
        win.document.write(html);
        win.document.close();
      }
    } catch (e: any) {
      toast.error((isZh ? '列印失敗：' : 'Print failed: ') + (e?.response?.data?.detail || e.message));
    } finally {
      setPrinting(false);
    }
  };

  const handleExportDocx = async () => {
    if (!selectedProposalId) return;
    setExporting(true);
    try {
      const title = data?.proposal_title || `Proposal_${selectedProposalId}`;
      const cleanTitle = title.replace(/<[^>]+>/g, '').replace(/[^a-zA-Z0-9\u4e00-\u9fff]/g, '_');
      const filename = `METH_Proposal_${cleanTitle}.docx`;
      await downloadNmethDocxReport(selectedProposalId, filename);
    } catch (e: any) {
      toast.error((isZh ? '產檔失敗：' : 'Export failed: ') + (e?.response?.data?.detail || e.message));
    } finally {
      setExporting(false);
    }
  };

  const getStatusColor = (pct: number) => {
    if (pct >= 80) return '#43a047';
    if (pct >= 40) return '#fb8c00';
    if (pct > 0) return '#e53935';
    return '#9e9e9e';
  };

  const getStatusLabel = (pct: number) => {
    if (pct >= 80) return isZh ? '完成' : 'Done';
    if (pct >= 40) return isZh ? '進行中' : 'In Progress';
    if (pct > 0) return isZh ? '未完成' : 'Incomplete';
    return isZh ? '未填寫' : 'Empty';
  };

  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header">
        <FunctionPageHeader
          funcCode="rpt_nmeth_proposal"
          subtitle={selectedProposal?.title ? selectedProposal.title.replace(/<[^>]+>/g, '') : undefined}
        />
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {canPrint && (
            <button
              className="btn-primary"
              onClick={handlePrint}
              disabled={printing || loading || !data}
            >
              {printing ? (isZh ? '列印中...' : 'Printing...') : `🖨️ ${isZh ? '列印' : 'Print'}`}
            </button>
          )}
          {canFile && (
            <button
              className="btn-primary"
              onClick={handleExportDocx}
              disabled={exporting || loading || !data}
              style={{ background: exporting ? '#90a4ae' : '#388e3c' }}
            >
              {exporting ? (isZh ? '產檔中...' : 'Exporting...') : `📄 ${isZh ? '產檔 (Word)' : 'Export (Word)'}`}
            </button>
          )}
        </div>
      </div>

      {error && <div className="error-message" style={{ marginBottom: 16 }}>{error}</div>}

      {!selectedProposalId && !loading && (
        <div style={{ padding: '48px', textAlign: 'center', color: '#999', fontSize: 16 }}>
          {isZh ? '請先在上方選擇方法論申請文件' : 'Please select a methodology proposal from the top bar'}
        </div>
      )}

      {loading && (
        <div className="loading">{isZh ? '載入中...' : 'Loading...'}</div>
      )}

      {data && !loading && (
        <div>
          {/* 整體完成度 */}
          <div style={{ background: '#f8f9fa', border: '1px solid #e0e0e0', borderRadius: 6, padding: '16px 20px', marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontSize: 15, fontWeight: 600, color: '#333' }}>
                {isZh ? '整體完成度' : 'Overall Progress'}
              </span>
              <span style={{ fontSize: 22, fontWeight: 700, color: getStatusColor(data.overall_percentage) }}>
                {data.overall_percentage}%
              </span>
            </div>
            <div style={{ height: 10, background: '#e0e0e0', borderRadius: 5, overflow: 'hidden' }}>
              <div style={{
                height: '100%',
                width: `${data.overall_percentage}%`,
                background: getStatusColor(data.overall_percentage),
                borderRadius: 5,
                transition: 'width 0.5s ease',
              }} />
            </div>
          </div>

          {/* 欄位清單 */}
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: 60 }}>#</th>
                <th>{isZh ? '欄位' : 'Field'}</th>
                <th style={{ width: 100, textAlign: 'center' }}>{isZh ? '狀態' : 'Status'}</th>
                <th style={{ width: 200, textAlign: 'right' }}>{isZh ? '完成度' : 'Progress'}</th>
              </tr>
            </thead>
            <tbody>
              {data.fields.map((field, idx) => {
                const pct = field.max_score > 0 ? Math.round(field.score / field.max_score * 100) : 0;
                const color = getStatusColor(pct);
                return (
                  <tr key={field.key}>
                    <td style={{ fontWeight: 600, color: '#666' }}>{idx + 1}</td>
                    <td>{isZh ? field.label_zh : field.label_en}</td>
                    <td style={{ textAlign: 'center' }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '2px 10px',
                        background: color + '20',
                        color,
                        borderRadius: 12,
                        fontSize: 12,
                        fontWeight: 600,
                        border: `1px solid ${color}40`,
                      }}>
                        {getStatusLabel(pct)}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'flex-end' }}>
                        <div style={{ flex: 1, height: 8, background: '#e0e0e0', borderRadius: 4, overflow: 'hidden', maxWidth: 120 }}>
                          <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 4 }} />
                        </div>
                        <span style={{ fontSize: 13, fontWeight: 600, color, minWidth: 50, textAlign: 'right' }}>
                          {field.score}/{field.max_score}
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default RptNmethProposalPage;
